import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

function useCountUp(target, duration = 400) {
  const [val, setVal] = useState(target);
  useEffect(() => {
    const start = val;
    const delta = target - start;
    if (delta === 0) return;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(start + delta * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return val;
}

function Line({ label, amount, muted }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-sm ${muted ? "text-[#4B5563]" : "text-[#0C1A1A]"}`}>{label}</span>
      <span className={`font-mono text-sm ${amount === 0 ? "text-[#9CA3AF]" : "text-[#0C1A1A] font-semibold"}`}>
        ${amount.toFixed(2)}
      </span>
    </div>
  );
}

function IncludedToggle({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pb-1">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="text-xs text-[#0EA5A4] font-semibold hover:underline inline-flex items-center gap-1"
        data-testid="base-price-included-toggle">
        <Info className="w-3 h-3" /> {t("wizard.included.toggle")}
      </button>
      {open && (
        <div className="mt-2 rounded-xl bg-[#0EA5A4]/5 border border-[#0EA5A4]/20 p-3" data-testid="base-price-included-details">
          <ul className="space-y-1">
            {t("wizard.included.items").map((item, i) => (
              <li key={i} className="flex gap-1.5 text-xs text-[#4B5563]">
                <span className="text-[#0EA5A4]">•</span><span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function PriceSummary({ breakdown, loading, hasZone, ctaLabel, onCtaClick, ctaDisabled, propertyType, customQuoteRequired }) {
  const { t } = useI18n();
  const total = breakdown?.total ?? 0;
  const animated = useCountUp(total);
  const baseLabel = propertyType === "other" ? t("wizard.summary.base_commercial") : t("wizard.summary.base");

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm" data-testid="price-summary">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-base font-bold">{t("wizard.summary.title")}</h3>
        {loading && <span className="text-xs text-[#0EA5A4] animate-pulse-soft">…</span>}
      </div>

      {customQuoteRequired ? (
        <p className="text-sm text-[#4B5563] py-6 text-center" data-testid="price-summary-custom-quote">{t("wizard.custom_quote.message")}</p>
      ) : !hasZone ? (
        <p className="text-sm text-[#4B5563] py-6 text-center">{t("wizard.summary.empty")}</p>
      ) : (
        <div className="divide-y divide-[#E5E7EB]">
          <div className="pb-2">
            <Line label={baseLabel} amount={breakdown?.base_price ?? 0} muted />
            <IncludedToggle t={t} />
            <Line label={t("wizard.summary.size")} amount={breakdown?.sqft_surcharge ?? 0} muted />
            <Line label={t("wizard.summary.bedrooms")} amount={breakdown?.bedroom_surcharge ?? 0} muted />
            <Line label={t("wizard.summary.bathrooms")} amount={breakdown?.bathroom_surcharge ?? 0} muted />
            <Line label={t("wizard.summary.half_bath")} amount={breakdown?.half_bathroom_surcharge ?? 0} muted />
            <Line label={t("wizard.summary.tasks")} amount={breakdown?.tasks_surcharge ?? 0} muted />
            <Line label={t("wizard.summary.zone")} amount={breakdown?.zone_surcharge ?? 0} muted />
            {breakdown?.discount > 0 && (
              <div className="flex items-center justify-between py-1.5" data-testid="discount-line">
                <span className="text-sm font-semibold text-emerald-700">Promo {breakdown.discount_code}</span>
                <span className="font-mono text-sm font-semibold text-emerald-700">-${breakdown.discount.toFixed(2)}</span>
              </div>
            )}
          </div>
          <div className="pt-3 flex items-center justify-between">
            <span className="font-display font-bold text-base">{t("wizard.summary.total")}</span>
            <AnimatePresence mode="popLayout">
              <motion.span key={Math.round(animated * 100)}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="font-mono text-3xl font-bold text-[#0EA5A4]" data-testid="price-total">
                ${animated.toFixed(2)}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>
      )}

      {ctaLabel && (
        <button onClick={onCtaClick} disabled={ctaDisabled}
          className="w-full mt-5 h-12 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold transition-colors active:scale-[0.98]"
          data-testid="price-summary-cta">
          {ctaLabel}
        </button>
      )}
    </div>
  );
}
